import React, { useState, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Link,
  CircularProgress,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AdminDataContext } from "../../useContext/AdminContext";
import { setAuthToken } from "../../api";

const LoginPageBigCommerce = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const { updateAdminData, snackbar } = useContext(AdminDataContext);

  const [values, setValues] = useState({
    email: "",
    password: "",
    showPassword: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: "",
    password: "",
  });

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
    // Clear error when user starts typing
    if (errors[prop]) {
      setErrors({ ...errors, [prop]: "" });
    }
  };

  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword,
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { email: "", password: "" };

    // Email validation
    if (!values.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(values.email)) {
      newErrors.email = "Please enter a valid email";
      isValid = false;
    }

    // Password validation
    if (!values.password) {
      newErrors.password = "Password is required";
      isValid = false;
    } else if (values.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleLogin = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/v1/users/login`,
        {
          email: values.email.trim(),
          password: values.password,
        }
      );

      const { accessToken, user } = res.data.data;

      // Check if user is blocked
      if (user.isBlocked) {
        snackbar(
          "Your account has been blocked. Please contact administrator.",
          "error"
        );
        navigate("/user-blocked");
        return;
      }

      // Store token for later use
      localStorage.setItem("accessToken", accessToken);
      setAuthToken(accessToken);
      updateAdminData(user);

      snackbar("Login successful!", "success");
      setIsAuthenticated(true);
      navigate("/");
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        "Login failed. Please check your credentials.";
      snackbar(errorMessage, "error");
      console.log("login error", error);
      if (error?.response?.status === 403) {
        navigate("/user-blocked");
        return;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleLogin();
    }
  };

  const handleForgotPassword = () => {
    snackbar(
      "Please contact your administrator to reset your password",
      "info"
    );
  };

  return (
    <>
      <style>{`
        .css-1yqec4o{
        box-sizing: border-box;    
        }
      `}</style>

      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          backgroundColor: "primary.main",
          padding: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Paper
          elevation={24}
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 450, md: 500 },
            borderRadius: { xs: 2, sm: 3, md: 4 },
            overflow: "hidden",
            backgroundColor: "#ffffff",
          }}
        >
          <Box
            sx={{
              padding: { xs: 3, sm: 4, md: 5 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                mb: 3,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <img
                src="/images/DrLamLogoColor.webp"
                alt="Company Logo"
                style={{
                  maxWidth: "220px",
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>

            {/* Login Heading */}
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                fontWeight: 600,
                color: "#333",
                textAlign: "center",
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
              }}
            >
              Login <span style={{ fontWeight: 400 }}>to your Bundle Kit</span>
            </Typography>

            {/* Email Field */}
            <TextField
              fullWidth
              placeholder="Enter your email"
              value={values.email}
              onChange={handleChange("email")}
              onKeyPress={handleKeyPress}
              error={!!errors.email}
              helperText={errors.email}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ color: "#999" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2.5,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#E8EAF6",
                  borderRadius: "30px",
                  "& fieldset": {
                    borderColor: "transparent",
                  },
                  "&:hover fieldset": {
                    borderColor: "#667eea",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#667eea",
                    borderWidth: 2,
                  },
                  "&.Mui-error fieldset": {
                    borderColor: "#d32f2f",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "14px 16px 14px 8px",
                  fontSize: "1rem",
                  fontFamily: "inherit",
                },
              }}
            />

            {/* Password Field */}
            <TextField
              fullWidth
              placeholder="Enter your password"
              type={values.showPassword ? "text" : "password"}
              value={values.password}
              onChange={handleChange("password")}
              onKeyPress={handleKeyPress}
              error={!!errors.password}
              helperText={errors.password}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: "#999" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      sx={{
                        color: "#999",
                        backgroundColor: "#E8EAF6",
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "#D1C4E9",
                        },
                      }}
                    >
                      {values.showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#E8EAF6",
                  borderRadius: "30px",
                  "& fieldset": {
                    borderColor: "transparent",
                  },
                  "&:hover fieldset": {
                    borderColor: "#667eea",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#667eea",
                    borderWidth: 2,
                  },
                  "&.Mui-error fieldset": {
                    borderColor: "#d32f2f",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "14px 16px 14px 8px",
                  fontSize: "1rem",
                  fontFamily: "inherit",
                },
              }}
            />

            {/* Login Button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleLogin}
              disabled={isLoading}
              sx={{
                mb: 2,
                borderRadius: "30px",
                padding: "12px 24px",
                fontSize: "1rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                background: "linear-gradient(135deg, #42A5F5 0%, #1565C0 100%)",
                boxShadow: "0 4px 15px rgba(66, 165, 245, 0.4)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)",
                  boxShadow: "0 6px 20px rgba(66, 165, 245, 0.6)",
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  background:
                    "#linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)",
                  color: "#fff",
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "LOGIN"
              )}
            </Button>

            {/* Forgot Password Link */}
            {/* <Link
            component="button"
            variant="body2"
            onClick={handleForgotPassword}
            disabled={isLoading}
            sx={{
              color: "#667eea",
              textDecoration: "none",
              fontWeight: 500,
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
              "&:disabled": {
                color: "#ccc",
                cursor: "not-allowed",
              },
            }}
          >
            Forgot Password?
          </Link> */}
          </Box>

          {/* Footer */}
          {/* <Box
          sx={{
            padding: 2,
            textAlign: "center",
            borderTop: "1px solid #f0f0f0",
            backgroundColor: "#fafafa",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#999",
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
            }}
          >
            © Copyright {new Date().getFullYear()} BigCommerce. All rights
            reserved.
          </Typography>
        </Box> */}
        </Paper>
      </Box>
    </>
  );
};

export default LoginPageBigCommerce;
